// Google / Microsoft SSO initiate buttons (shared by login and register landing pages)
import React, { useState } from 'react';
import { Box, Button, CircularProgress, Stack, SvgIcon } from '@mui/material';
import {
  fetchSsoInitiateUrl,
  getSsoCallbackRedirectUri,
  type SsoProvider,
} from '../../services/ssoService';

/** Minimal Google "G" mark for the button label */
function GoogleIcon() {
  return (
    <SvgIcon sx={{ width: 20, height: 20, mr: 1 }} viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </SvgIcon>
  );
}

/** Microsoft four-square mark */
function MicrosoftIcon() {
  return (
    <SvgIcon sx={{ width: 20, height: 20, mr: 1 }} viewBox="0 0 24 24">
      <path fill="#f25022" d="M1 1h10v10H1z" />
      <path fill="#00a4ef" d="M13 1h10v10H13z" />
      <path fill="#7fba00" d="M1 13h10v10H1z" />
      <path fill="#ffb900" d="M13 13h10v10H13z" />
    </SvgIcon>
  );
}

interface SsoProviderButtonsProps {
  /** When false, buttons are disabled (e.g. while resolving encrypted invite on /register) */
  allowClick?: boolean;
  /** Passed to initiate only for invite registration; omit / undefined for login */
  inviteId?: string;
  onError: (message: string) => void;
}

const SsoProviderButtons: React.FC<SsoProviderButtonsProps> = ({
  allowClick = true,
  inviteId,
  onError,
}) => {
  const [loading, setLoading] = useState<SsoProvider | null>(null);

  const startSso = async (provider: SsoProvider) => {
    if (!allowClick || loading) return;
    setLoading(provider);
    onError('');
    try {
      const redirectUri = getSsoCallbackRedirectUri();
      const { authorization_url: authorizationUrl } = await fetchSsoInitiateUrl(provider, {
        redirectUri,
        inviteId: inviteId || undefined,
      });
      if (!authorizationUrl) {
        throw new Error('No authorization URL returned from server');
      }
      window.location.assign(authorizationUrl);
    } catch (e) {
      setLoading(null);
      onError(e instanceof Error ? e.message : 'Sign-in could not be started');
    }
  };

  const disabled = !allowClick || !!loading;

  return (
    <Stack spacing={1.5}>
      <Button
        fullWidth
        variant="outlined"
        size="large"
        disabled={disabled}
        onClick={() => startSso('google')}
        id="google-sso-button"
        sx={{
          py: 1.25,
          borderColor: 'grey.300',
          color: 'text.primary',
          fontWeight: 700,
          bgcolor: 'background.paper',
          textTransform: 'none',
          '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
        }}
        startIcon={
          loading === 'google' ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />
        }
      >
        Continue with Google
      </Button>
      <Button
        fullWidth
        variant="outlined"
        size="large"
        disabled={disabled}
        onClick={() => startSso('microsoft')}
        sx={{
          py: 1.25,
          borderColor: 'grey.300',
          color: 'text.primary',
          fontWeight: 700,
          bgcolor: 'background.paper',
          textTransform: 'none',
          '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
        }}
        startIcon={
          loading === 'microsoft' ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <MicrosoftIcon />
          )
        }
      >
        Continue with Microsoft
      </Button>
    </Stack>
  );
};

export default SsoProviderButtons;
